// Extracted from App.js during Phase 4 refactor (sprint 2026-06-20).
// Two top-level overlays mounted by App: stale-URL update banner + session
// expired modal.
import React from 'react';
import { WifiOff, LogIn } from 'lucide-react';
import { useAuth } from '../context/AppContext';

export function StaleUrlBanner({ expectedUrl, onDismiss }) {
  // Iter. 406.fj — Toast-style banner anchored to the BOTTOM (not blocking
  // header area), narrow width on desktop so it never overlays primary
  // navigation. Dismissible at all times via the × button OR auto-dismiss
  // after 12s (handled in App). If `expectedUrl` is missing for any reason
  // we render the banner WITHOUT the "Mettre à jour" link instead of
  // crashing.
  const hasUrl = typeof expectedUrl === 'string' && expectedUrl.startsWith('http');

  // Iter. 406.fr.67 — Update flow rewritten. Previously the button was an
  // `<a target="_blank">` opening the correct backend URL in a NEW TAB —
  // that created a parallel session on a different origin, re-triggering
  // the ToS acceptance flow AND sometimes leaving the old PWA tab broken
  // (users reported "app won't open, must reinstall"). Now we:
  //   1. Unregister every service worker on the current origin
  //   2. Delete every cache
  //   3. PRESERVE session_token + language + user_profile
  //   4. Redirect SAME tab to expectedUrl + current pathname + cache-buster
  const handleUpdateClick = React.useCallback(async (e) => {
    e.preventDefault();
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
      }
    } catch (_) { /* ignore */ }
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    } catch (_) { /* ignore */ }
    try {
      const target = new URL(expectedUrl);
      // Keep the user on the same page after the migration.
      target.pathname = window.location.pathname || '/';
      target.searchParams.set('_yx_recover', String(Date.now()));
      window.location.replace(target.toString());
    } catch (_) {
      // Fallback: at least try a reload on the current origin.
      try { window.location.reload(); } catch (__) { /* ignore */ }
    }
  }, [expectedUrl]);

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-1.5rem)] sm:w-auto sm:max-w-md bg-amber-500/95 backdrop-blur-sm text-amber-950 px-3 py-2 shadow-lg border border-amber-600/40 rounded-xl"
      data-testid="stale-url-banner"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5">
        <span className="text-base shrink-0" aria-hidden>⚠️</span>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs sm:text-sm font-bold leading-tight">
            Nouvelle version disponible
          </p>
          <p className="text-[10px] sm:text-[11px] opacity-80 leading-tight">
            L&apos;app reste utilisable.
          </p>
        </div>
        {hasUrl && (
          <button
            type="button"
            onClick={handleUpdateClick}
            className="shrink-0 inline-block bg-amber-950 hover:bg-amber-900 text-amber-50 font-bold text-[10px] sm:text-xs px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
            data-testid="stale-url-redirect-link"
          >
            Mettre à jour
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fermer cette notification"
          data-testid="stale-url-dismiss-btn"
          className="shrink-0 w-6 h-6 rounded-full hover:bg-amber-950/15 flex items-center justify-center transition-colors"
        >
          <span className="text-amber-950 text-base leading-none font-bold" aria-hidden>×</span>
        </button>
      </div>
    </div>
  );
}

export function SessionExpiredOverlay() {
  const { sessionExpired, reconnect, dismissSessionExpired, user } = useAuth();

  // Never show if user is authenticated or sessionExpired is false
  if (!sessionExpired || user) return null;

  return (
    <div className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="session-expired-overlay">
      <div className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
        <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
          <WifiOff className="w-8 h-8 text-orange-400" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          Session expirée
        </h2>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Ta session a expiré pour des raisons de sécurité. Reconnecte-toi pour continuer ton Extra Mile.
        </p>
        <button
          onClick={reconnect}
          className="w-full bg-primary text-primary-foreground font-bold uppercase py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 mb-3 hover:opacity-90 transition-opacity"
          data-testid="session-expired-reconnect-btn"
        >
          <LogIn className="w-5 h-5" />
          Se reconnecter
        </button>
        <button
          onClick={dismissSessionExpired}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="session-expired-dismiss-btn"
        >
          Retour à l&apos;accueil
        </button>
      </div>
    </div>
  );
}
